/* Shared reschedule discussion UI for teacher & parent portals.
   Flow: pick class -> reason -> pick an AVAILABLE slot (only free options shown)
   -> other party accepts / declines / counter-proposes -> both agree
   -> admin approves the final slot. */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { useList, errMsg, dayName } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  RefreshCcw, ArrowLeft, Check, X, MessageSquareText, Send, CalendarClock,
  AlertTriangle, Gauge, ArrowRight, MessageCircle, Timer,
} from "lucide-react";

// ------------------------------------------------------------------ special one-time slot confirm
export const isSpecialSlotErr = (e) => !!e?.response?.data?.detail?.can_open_special;

export function SpecialSlotDialog({ open, onConfirm, onCancel, busy }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md" data-testid="special-slot-dialog">
        <DialogHeader><DialogTitle className="font-display">Open a special one-time slot?</DialogTitle></DialogHeader>
        <p className="text-sm">
          This time is outside your normal availability. Open it as a one-time special slot for this student?
        </p>
        <p className="text-xs text-muted-foreground">
          It applies only to this student and this replacement — your normal weekly availability is not
          changed, and it is removed automatically if you withdraw or change the proposal. Confirmed
          timetable clashes can never be overridden.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="special-slot-cancel">Cancel</Button>
          <Button onClick={onConfirm} disabled={busy} data-testid="special-slot-confirm">
            {busy ? "Opening..." : "Open Special Slot"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------ slot picker
export function SlotPicker({ scheduleId, requestId, value, onChange, showStudio = true, holdOnSelect = false }) {
  const [date, setDate] = useState(value?.date || "");
  const [slots, setSlots] = useState(null);
  const [loading, setLoading] = useState(false);
  const [weeks, setWeeks] = useState(4);
  const [days, setDays] = useState(null);

  // only days the teacher is actually available are offered
  useEffect(() => {
    if (!scheduleId) return;
    api.get("/reschedule/available-days", { params: { schedule_id: scheduleId, weeks } })
      .then((r) => {
        setDays((prev) => {
          if (prev && r.data.days.length === prev.length && weeks > 4) {
            toast.info(`No further available days found up to ${weeks} weeks ahead`);
          }
          return r.data.days;
        });
      })
      .catch((e) => toast.error(errMsg(e)));
  }, [scheduleId, weeks]);

  useEffect(() => {
    if (!date || !scheduleId) return;
    loadSlots(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, scheduleId]);

  const loadSlots = (d) => {
    setLoading(true);
    api.get(`/reschedule/available-slots`, { params: { schedule_id: scheduleId, date: d, request_id: requestId } })
      .then((r) => setSlots(r.data.slots))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  };

  const pick = async (slot, studio) => {
    if (holdOnSelect) {
      // concurrent booking protection: place a 5-minute hold on selection
      try {
        await api.post("/slot-holds", {
          schedule_id: scheduleId, request_id: requestId || null,
          date, start_time: slot.start_time, end_time: slot.end_time,
        });
      } catch (e) {
        toast.error(errMsg(e), { duration: 6000 });
        onChange(null);
        loadSlots(date);
        return;
      }
    }
    onChange({ date, start_time: slot.start_time, end_time: slot.end_time, studio_id: studio?.id || null, studio_name: studio?.name || null });
  };
  const fmtDay = (d) => new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Available days (based on teacher availability)
        </Label>
        {!days ? <Spinner /> : days.length === 0 ? (
          <p className="text-sm text-muted-foreground border border-dashed rounded-md p-3">
            The teacher has no availability in the next {weeks} weeks — try showing more dates or ask the teacher to propose.
          </p>
        ) : (
          <div className="max-h-32 overflow-y-auto border rounded-md p-2 flex flex-wrap gap-1.5" data-testid="available-days-list">
            {days.map((d) => (
              <button key={d.date} type="button" data-testid={`available-day-${d.date}`}
                onClick={() => { setDate(d.date); onChange(null); }}
                className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${
                  date === d.date ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`}>
                {fmtDay(d)}
              </button>
            ))}
          </div>
        )}
        <button type="button" onClick={() => setWeeks((w) => Math.min(w + 4, 52))}
          className="text-xs text-secondary font-semibold hover:underline" data-testid="slot-show-more">
          Show more dates (earlier booking = more time for the teacher to prepare)
        </button>
      </div>
      {loading && <Spinner />}
      {!loading && date && slots && slots.length === 0 && (
        <p className="text-sm text-muted-foreground border border-dashed rounded-md p-3" data-testid="no-slots-message">
          No free times on {date} — teacher, student or studios are fully booked. Try another day.
        </p>
      )}
      {!loading && slots && slots.length > 0 && (
        <div className="max-h-56 overflow-y-auto space-y-1.5 border rounded-md p-2" data-testid="slot-list">
          {slots.map((s) => showStudio ? (
            <div key={s.start_time}
              className={`rounded-md border p-2 ${value?.start_time === s.start_time ? "border-primary bg-accent" : ""}`}>
              <p className="text-sm font-mono font-semibold">{s.start_time}–{s.end_time}</p>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {s.available_studios.map((st) => (
                  <button key={st.id} type="button" data-testid={`slot-${s.start_time}-${st.id}`}
                    onClick={() => pick(s, st)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      value?.start_time === s.start_time && value?.studio_id === st.id
                        ? "bg-primary text-primary-foreground border-primary"
                        : "hover:bg-muted"}`}>
                    {st.name}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <button key={s.start_time} type="button" data-testid={`slot-${s.start_time}`}
              onClick={() => pick(s, null)}
              className={`w-full text-left rounded-md border p-2 transition-colors ${
                value?.start_time === s.start_time ? "border-primary bg-accent" : "hover:bg-muted"}`}>
              <p className="text-sm font-mono font-semibold">{s.start_time}–{s.end_time}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Studio assigned by the teacher on acceptance
              </p>
            </button>
          ))}
        </div>
      )}
      {value && (
        <p className="text-xs text-[#2D6A4F] font-semibold" data-testid="selected-slot">
          Selected: {value.date} {value.start_time}–{value.end_time}
          {value.studio_name ? ` · ${value.studio_name}` : " · studio assigned by teacher"}
        </p>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ quota banner
export function QuotaBanner({ quota }) {
  if (!quota) return null;
  // student-cycle shape (parents): {scope:'student_cycle', children:[...]} or single child
  if (quota.scope === "student_cycle") {
    const rows = quota.children || [quota];
    return (
      <div className={`rounded-lg border p-4 mb-6 flex items-start gap-3 ${quota.exceeded ? "border-destructive/40 bg-[#FFEDEC]" : "bg-card"}`}
        data-testid="quota-banner">
        <Gauge className={`h-5 w-5 shrink-0 mt-0.5 ${quota.exceeded ? "text-destructive" : "text-secondary"}`} />
        <div className="text-sm space-y-0.5">
          <p className="font-semibold">Student request quota — 7 per 12-month cycle (leave + reschedule)</p>
          {rows.map((c) => (
            <p key={c.student_id} className={`text-xs ${c.exceeded ? "text-destructive font-semibold" : "text-muted-foreground"}`}
              data-testid={`cycle-quota-${c.student_id}`}>
              {c.student_name || "Student"}: {c.used}/{c.limit} used · cycle {c.cycle_start} → {c.cycle_end}
              {c.exceeded ? " · limit reached — admin override needed" : ""}
            </p>
          ))}
          <p className="text-xs text-muted-foreground">Teacher leave never uses your child's quota.</p>
        </div>
      </div>
    );
  }
  return (
    <div className={`rounded-lg border p-4 mb-6 flex items-center gap-3 ${quota.exceeded ? "border-destructive/40 bg-[#FFEDEC]" : "bg-card"}`}
      data-testid="quota-banner">
      <Gauge className={`h-5 w-5 shrink-0 ${quota.exceeded ? "text-destructive" : "text-secondary"}`} />
      <div className="text-sm">
        <p className="font-semibold">
          Reschedule quota — {quota.month_used}/{quota.month_limit ?? "∞"} this month · {quota.year_used}/{quota.year_limit ?? "∞"} this year
        </p>
        <p className="text-xs text-muted-foreground">
          {quota.exceeded
            ? quota.allow_admin_override
              ? "Limit reached — new requests will need an admin override."
              : "Limit reached — new requests are blocked."
            : `Policy: ${quota.policy_name}`}
        </p>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ replacement minute ledger
// ------------------------------------------------------------------ occurrence picker (original lesson)
export function OccurrencePicker({ scheduleId, value, onChange }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!scheduleId) { setData(null); return; }
    api.get("/reschedule/schedule-occurrences", { params: { schedule_id: scheduleId, count: 5 } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [scheduleId]);
  if (!scheduleId) return null;
  if (!data) return <Spinner />;
  const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" });
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Which lesson? ({data.day_name}s at {data.start_time})
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid="occurrence-select"><SelectValue placeholder="Select the lesson date..." /></SelectTrigger>
        <SelectContent>
          {data.occurrences.map((o) => (
            <SelectItem key={o.date} value={o.date} disabled={o.has_active_request}>
              {data.day_name} {fmt(o.date)} · {o.start_time}–{o.end_time}
              {o.has_active_request ? " — already requested" : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">Only your actual upcoming lesson dates can be selected.</p>
    </div>
  );
}

// ------------------------------------------------------------------ open replacement slots (first come first serve)
export function OpenSlotsPanel({ detail, role, onClaimed }) {
  const isParent = role !== "teacher";
  const { data: slots, reload } = useList(
    isParent ? `/replacement-slots?request_id=${detail.id}` : "/replacement-slots", [detail.id]);
  const [busyId, setBusyId] = useState(null);
  const list = (slots || []).filter((s) => (isParent ? s.status === "open" : true));
  if (list.length === 0) return null;

  const claim = async (slotId) => {
    setBusyId(slotId);
    try {
      await api.post(`/replacement-slots/${slotId}/claim`, { reschedule_request_id: detail.id });
      toast.success("Slot claimed! Replacement confirmed — timetables updated.");
      onClaimed?.();
    } catch (e) { toast.error(errMsg(e), { duration: 7000 }); reload(); }
    finally { setBusyId(null); }
  };
  const cancelSlot = async (slotId) => {
    try { await api.delete(`/replacement-slots/${slotId}`); toast.success("Slot withdrawn"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

  return (
    <div className="rounded-lg border border-secondary/50 bg-accent/50 p-4 mb-6" data-testid="open-slots-panel">
      <p className="text-sm font-semibold mb-2 flex items-center gap-1.5">
        <CalendarClock className="h-4 w-4 text-secondary" />
        {isParent ? "Teacher's open replacement slots — first come, first served" : "Your open replacement slots"}
      </p>
      <div className="space-y-1.5">
        {list.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center gap-2 text-sm bg-card rounded-md border px-3 py-2"
            data-testid={`open-slot-${s.id}`}>
            <span className="font-mono font-semibold">{fmt(s.date)} · {s.start_time}–{s.end_time}</span>
            <span className="text-xs text-muted-foreground">{s.duration_minutes} min</span>
            {s.scope === "student" && <span className="text-xs px-2 py-0.5 rounded-full bg-[#FFF3E0] text-[#B45309]">reserved: {s.student_name}</span>}
            {!isParent && s.status !== "open" && <StatusBadge status={s.status} />}
            {s.status === "claimed" && !isParent && <span className="text-xs text-muted-foreground">by {s.claimed_by_student_name}</span>}
            <span className="flex-1" />
            {isParent ? (
              <Button size="sm" className="h-7" disabled={busyId === s.id} onClick={() => claim(s.id)}
                data-testid={`claim-slot-${s.id}`}>
                {busyId === s.id ? "Claiming..." : "Claim this slot"}
              </Button>
            ) : s.status === "open" && (
              <Button size="sm" variant="ghost" className="h-7 text-destructive" onClick={() => cancelSlot(s.id)}
                data-testid={`cancel-slot-${s.id}`}>Withdraw</Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function OpenSlotsDialog({ detail, open, onOpenChange, onDone }) {
  // Local-date formatter — avoids toISOString() UTC shift that mis-buckets
  // Malaysia (UTC+8) dates by one day.
  const fmtLocal = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return fmtLocal(d);
  });
  const [cal, setCal] = useState(null);
  const [selDate, setSelDate] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [scope, setScope] = useState("all");
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [suggBusy, setSuggBusy] = useState(false);
  // meta[i] = { from_smart_suggestion, suggestion_kind, outside_normal_availability }
  const [pendingMeta, setPendingMeta] = useState([]);
  const [pickedKind, setPickedKind] = useState(null); // 'before_first_class' | 'between_classes' | 'after_last_class' | 'manual' | null

  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart + "T00:00:00"); d.setDate(d.getDate() + i);
    return d.toISOString().slice(0, 10);
  });

  useEffect(() => {
    if (!open) return;
    api.get("/teacher/calendar", { params: { from: weekDates[0], to: weekDates[6] } })
      .then((r) => setCal(r.data)).catch(() => setCal({ events: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, weekStart]);

  // fetch smart suggestions when a day is picked
  useEffect(() => {
    if (!open || !selDate) { setSuggestions(null); return; }
    setSuggBusy(true);
    api.get("/replacement-slots/suggestions", { params: { date: selDate } })
      .then((r) => setSuggestions(r.data))
      .catch(() => setSuggestions({ suggestions: [], busy_blocks: [], window: null }))
      .finally(() => setSuggBusy(false));
  }, [open, selDate]);

  const shiftWeek = (n) => {
    const d = new Date(weekStart + "T00:00:00"); d.setDate(d.getDate() + n * 7);
    setWeekStart(fmtLocal(d));
  };
  const eventsOn = (date) => (cal?.events || []).filter((e) => e.date === date)
    .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  const fmt = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

  const _mins = (t) => { const [h, m] = t.split(":"); return +h * 60 + +m; };
  const _hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

  // pick a smart-suggestion chip: pre-fills from/to at the full gap width
  const pickSuggestion = (s) => {
    setFrom(s.start_time); setTo(s.end_time); setPickedKind(s.kind);
  };
  // pick a duration chip: trims to selected duration inside the current from-window
  const pickDuration = (mins) => {
    if (!from) { toast.error("Pick a start time or a smart suggestion first"); return; }
    setTo(_hhmm(_mins(from) + mins));
  };

  // is current from-to outside teacher's normal recurring availability?
  const currentOutsideNormal = (() => {
    if (!from || !to || !suggestions) return false;
    // Try to match a suggestion whose window contains the current from-to
    const s = (suggestions.suggestions || []).find(
      (x) => x.start_time <= from && x.end_time >= to);
    return s ? !!s.outside_normal_availability : false;
  })();

  const addSlot = () => {
    if (!selDate || !from || !to) { toast.error("Pick a day and set the time range"); return; }
    if (_mins(to) - _mins(from) < 30) { toast.error("Standalone replacement must be at least 30 minutes"); return; }
    setPending((p) => [...p, { date: selDate, start_time: from, end_time: to }]);
    setPendingMeta((m) => [...m, {
      from_smart_suggestion: pickedKind && pickedKind !== "manual",
      suggestion_kind: pickedKind || "manual",
      outside_normal_availability: currentOutsideNormal,
    }]);
    setFrom(""); setTo(""); setPickedKind(null);
  };
  const submit = async () => {
    if (pending.length === 0) { toast.error("Add at least one slot"); return; }
    setBusy(true);
    try {
      const slotsPayload = pending.map((s, i) => ({
        ...s,
        from_smart_suggestion: !!pendingMeta[i]?.from_smart_suggestion,
        suggestion_kind: pendingMeta[i]?.suggestion_kind || "manual",
      }));
      const { data } = await api.post("/replacement-slots", {
        slots: slotsPayload, scope,
        student_id: scope === "student" ? detail.student_id : null,
        source_leave_id: detail.source_leave_id || null,
      });
      if (data.rejected.length) {
        toast.warning(`${data.created.length} opened · ${data.rejected.length} rejected: ${data.rejected[0].reason}`, { duration: 8000 });
      } else {
        toast.success(`${data.created.length} slot(s) opened — families notified (first come, first served)`);
      }
      setPending([]); setPendingMeta([]); onOpenChange(false); onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="open-slots-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Open replacement slots</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">
          Your week is shown below — pick free gaps before, between or after your classes, or any other day.
          Families claim first come, first served; the system re-checks trials, enrolments and bookings at claim time.
        </p>
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" onClick={() => shiftWeek(-1)} data-testid="slots-prev-week">← Prev week</Button>
          <p className="text-sm font-semibold font-mono">{fmt(weekDates[0])} – {fmt(weekDates[6])}</p>
          <Button variant="outline" size="sm" onClick={() => shiftWeek(1)} data-testid="slots-next-week">Next week →</Button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center" data-testid="week-grid">
          {weekDates.map((d) => (
            <button key={d} type="button" onClick={() => setSelDate(d)} data-testid={`week-day-${d}`}
              className={`rounded-md border p-1.5 min-h-[110px] text-left align-top transition-colors ${
                selDate === d ? "border-primary bg-accent" : "hover:bg-muted/50"}`}>
              <p className="text-[10px] font-bold text-center mb-1">{fmt(d)}</p>
              <div className="space-y-0.5">
                {eventsOn(d).slice(0, 5).map((e, i) => (
                  <p key={i} className="text-[9px] font-mono bg-primary/10 rounded px-1 py-0.5 truncate"
                    title={`${e.start_time}–${e.end_time} ${e.title || e.student_name || e.kind || ""}`}>
                    {e.start_time} {e.title || e.student_name || e.kind || "busy"}
                  </p>
                ))}
                {eventsOn(d).length === 0 && <p className="text-[9px] text-muted-foreground text-center">free</p>}
              </div>
            </button>
          ))}
        </div>
        {selDate && (
          <div className="rounded-md border border-dashed border-secondary/60 bg-accent/40 p-3 space-y-2"
            data-testid="smart-suggestions-panel">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Smart suggestions for {fmt(selDate)}
              {suggestions?.window && ` · window ${suggestions.window.start}–${suggestions.window.end}`}
            </p>
            {suggBusy ? (
              <p className="text-xs text-muted-foreground">Scanning your calendar…</p>
            ) : (suggestions?.suggestions?.length ? (
              <div className="flex flex-wrap gap-1.5" data-testid="suggestion-chips">
                {suggestions.suggestions.map((s, i) => (
                  <button key={i} type="button" onClick={() => pickSuggestion(s)}
                    data-testid={`suggestion-chip-${s.kind}-${i}`}
                    className="text-xs px-2.5 py-1 rounded-full border bg-card hover:bg-primary hover:text-primary-foreground transition-colors font-mono">
                    {s.label}
                    {s.outside_normal_availability && <span className="ml-1 text-[10px]">⚠</span>}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No free windows within the replacement operating window.</p>
            ))}
            <div className="flex flex-wrap gap-1.5 pt-1" data-testid="duration-chips">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground self-center mr-1">Duration:</span>
              {[30, 45, 60, 90].map((m) => (
                <button key={m} type="button" onClick={() => pickDuration(m)}
                  data-testid={`duration-chip-${m}`}
                  className="text-xs px-2 py-0.5 rounded-full border bg-card hover:bg-muted/60 font-mono">
                  {m} min
                </button>
              ))}
            </div>
            {currentOutsideNormal && (
              <p className="text-xs text-[#B45309] bg-[#FFF3E0] border border-[#F59E0B]/40 rounded px-2 py-1.5 flex items-start gap-1.5"
                data-testid="outside-normal-warning">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>Outside your normal recurring availability. Open this slot only if you are genuinely available at this time.</span>
              </p>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-muted-foreground">Selected day</Label>
            <Input value={selDate ? fmt(selDate) : ""} readOnly placeholder="Click a day above" data-testid="slots-selected-day" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-muted-foreground">From</Label>
            <Input type="time" value={from} onChange={(e) => { setFrom(e.target.value); setPickedKind("manual"); }} data-testid="slots-from-time" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-muted-foreground">To</Label>
            <Input type="time" value={to} onChange={(e) => { setTo(e.target.value); setPickedKind((k) => k || "manual"); }} data-testid="slots-to-time" />
          </div>
          <Button variant="outline" onClick={addSlot} data-testid="slots-add-button">+ Add slot</Button>
        </div>
        <div className="flex items-center gap-3">
          <Label className="text-xs font-semibold text-muted-foreground shrink-0">Who can claim?</Label>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-64" data-testid="slots-scope-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All my affected students (first come first serve)</SelectItem>
              <SelectItem value="student">Only {detail.student_name}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {pending.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="pending-slots">
            {pending.map((s, i) => (
              <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-primary text-primary-foreground font-mono">
                {fmt(s.date)} {s.start_time}–{s.end_time}
                {pendingMeta[i]?.outside_normal_availability && <span className="ml-1" title="Outside normal availability">⚠</span>}
                <button type="button" className="ml-1.5 text-secondary"
                  onClick={() => {
                    setPending((p) => p.filter((_, j) => j !== i));
                    setPendingMeta((m) => m.filter((_, j) => j !== i));
                  }}>×</button>
              </span>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || pending.length === 0} data-testid="slots-submit-button">
            {busy ? "Opening..." : `Open ${pending.length} slot(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------ replacement minute ledger
export function MinuteLedgerCard({ detail, viewerSide, onAddExtension }) {
  if (detail.minutes_owed == null) return <div className="hidden sm:block" />;
  const done = detail.minutes_completed || 0;
  const owed = detail.minutes_owed || 0;
  const remaining = detail.minutes_remaining ?? Math.max(0, owed - done);
  const statusStyles = {
    fully_replaced: "bg-[#E7F3EC] text-[#2D6A4F]",
    partially_replaced: "bg-[#FFF3E0] text-[#B45309]",
    pending: "bg-muted text-muted-foreground",
  };
  return (
    <div className="rounded-lg border bg-card p-4" data-testid="minute-ledger-card">
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
        <Timer className="h-3 w-3" />Replacement minutes
      </p>
      <p className="font-mono text-sm" data-testid="minute-ledger-values">
        {done}/{owed} min completed · {remaining} remaining
      </p>
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${statusStyles[detail.replacement_status] || statusStyles.pending}`}>
          {(detail.replacement_status || "pending").replace(/_/g, " ")}
        </span>
        {remaining > 0 && viewerSide !== "parent" && (
          <Button variant="outline" size="sm" className="h-6 text-xs px-2" onClick={onAddExtension}
            data-testid="add-extension-button">
            + Add extension
          </Button>
        )}
      </div>
      {remaining > 0 && (
        <p className="text-xs text-muted-foreground mt-1">
          Remaining minutes can be cleared with a 15-min extension attached to a future lesson (no standalone class under 30 min).
        </p>
      )}
    </div>
  );
}
const SOURCE_LABELS = {
  teacher_leave: "Teacher leave", teacher_mc: "Teacher MC", emergency_leave: "Emergency leave",
  parent_request: null, teacher_request: null, admin_created: "Admin created",
};


// ------------------------------------------------------------------ P4: adjacent replacement + swap/shift
function _durMin(st, en) {
  const [h1, m1] = st.split(":").map(Number);
  const [h2, m2] = en.split(":").map(Number);
  return (h2 * 60 + m2) - (h1 * 60 + m1);
}
function _addMin(t, m) {
  const [h, mi] = t.split(":").map(Number);
  const tot = h * 60 + mi + m;
  return `${String(Math.floor(tot / 60)).padStart(2, "0")}:${String(tot % 60).padStart(2, "0")}`;
}

function SwapShiftDialog({ open, onOpenChange, candidate, requestId, onDone }) {
  const [newStart, setNewStart] = useState("");
  const [newEnd, setNewEnd] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && candidate?.conflict_with_student) {
      setNewStart(candidate.end_time);
      const dur = _durMin(candidate.conflict_with_student.start_time, candidate.conflict_with_student.end_time);
      setNewEnd(_addMin(candidate.end_time, dur));
      setNote("");
    }
  }, [open, candidate]);
  const submit = async () => {
    if (!newStart || !newEnd) { toast.error("Set Student B's new time"); return; }
    setBusy(true);
    try {
      await api.post("/swap-shift-proposals", {
        student_a_reschedule_id: requestId,
        adjacent_date: candidate.date,
        adjacent_start_time: candidate.start_time,
        adjacent_end_time: candidate.end_time,
        student_b_class_schedule_id: candidate.conflict_with_student.class_schedule_id,
        student_b_new_start_time: newStart,
        student_b_new_end_time: newEnd,
        note: note || null,
      });
      toast.success("Swap proposal sent — waiting for parent to approve");
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!candidate?.conflict_with_student) return null;
  const cws = candidate.conflict_with_student;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="swap-shift-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Propose swap / shift</DialogTitle>
          <DialogDescription>
            {cws.student_name}&apos;s parent must approve before their lesson time is changed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="rounded-md border bg-muted/40 p-2.5 text-xs space-y-0.5">
            <p><span className="font-semibold">Adjacent replacement:</span> {candidate.date} {candidate.start_time}–{candidate.end_time}</p>
            <p><span className="font-semibold">{cws.student_name}&apos;s current lesson:</span> {cws.start_time}–{cws.end_time}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">B new start</Label>
              <Input type="time" value={newStart} onChange={(e) => setNewStart(e.target.value)}
                data-testid="swap-b-start" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">B new end</Label>
              <Input type="time" value={newEnd} onChange={(e) => setNewEnd(e.target.value)}
                data-testid="swap-b-end" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Message to {cws.student_name}&apos;s parent (optional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              data-testid="swap-note" placeholder="Explain why you're proposing the shift..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="swap-submit">
            {busy ? "Sending..." : "Send proposal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SwapShiftInbox({ role }) {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const load = () => {
    api.get("/swap-shift-proposals").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  };
  useEffect(() => { load(); }, []);
  const decide = async (id, decision) => {
    setBusyId(id);
    try {
      await api.post(`/swap-shift-proposals/${id}/decide`, { decision });
      toast.success(`Proposal ${decision === "approve" ? "approved" : "rejected"}`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusyId(null); }
  };
  if (!items) return null;
  const pending = items.filter((i) => i.status === "pending_parent_response");
  const relevant = role === "parent" ? pending : pending;  // teachers also see their pending
  if (relevant.length === 0) return null;
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 mb-4"
      data-testid="swap-shift-inbox">
      <p className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-1.5">
        <RefreshCcw className="h-4 w-4" />
        {relevant.length} swap / shift proposal{relevant.length > 1 ? "s" : ""} awaiting your response
      </p>
      <div className="space-y-2">
        {relevant.map((p) => (
          <div key={p.id} className="rounded-md border bg-card px-3 py-2 text-xs flex flex-wrap items-center gap-2"
            data-testid={`swap-inbox-${p.id}`}>
            <span className="font-semibold">{p.student_b_name}</span>
            <span>on {p.adjacent_date}</span>
            <span className="font-mono">
              {p.student_b_original_start_time}–{p.student_b_original_end_time} → {p.student_b_new_start_time}–{p.student_b_new_end_time}
            </span>
            <span className="text-muted-foreground text-[10px]">
              (to free {p.adjacent_start_time}–{p.adjacent_end_time} for {p.student_a_name})
            </span>
            <span className="flex-1" />
            {role === "parent" && (
              <>
                <Button size="sm" variant="outline" className="h-7"
                  onClick={() => decide(p.id, "reject")} disabled={busyId === p.id}
                  data-testid={`swap-reject-${p.id}`}>
                  Reject
                </Button>
                <Button size="sm" className="h-7"
                  onClick={() => decide(p.id, "approve")} disabled={busyId === p.id}
                  data-testid={`swap-approve-${p.id}`}>
                  Approve shift
                </Button>
              </>
            )}
            {role !== "parent" && (
              <span className="text-[10px] italic text-muted-foreground">
                Waiting for {p.student_b_name}&apos;s parent
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


export function AdjacentSuggestionsPanel({ requestId, detail, onDone }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [swapOpen, setSwapOpen] = useState(false);
  const [swapCandidate, setSwapCandidate] = useState(null);
  const [special, setSpecial] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => {
    setLoading(true);
    api.get(`/reschedule-requests/${requestId}/adjacent-suggestions`)
      .then((r) => setData(r.data))
      .catch(() => setData({ suggestions: [] }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [requestId]);
  const bookClean = async (s, openSpecial = false) => {
    setBusy(true);
    try {
      await api.post(`/reschedule-requests/${requestId}/proposals`, {
        proposed_date: s.date, proposed_start_time: s.start_time, proposed_end_time: s.end_time,
        proposed_studio_id: null, // engine auto-assigns a genuinely free studio
        open_special_slot: openSpecial,
        message: `Adjacent replacement ${s.kind} normal class (${s.combined_span.start_time}–${s.combined_span.end_time})`,
      });
      toast.success(openSpecial
        ? "Special one-time slot proposed — parent will confirm"
        : "Adjacent replacement proposed — parent will confirm");
      setSpecial(null);
      onDone?.();
      load();
    } catch (e) {
      if (isSpecialSlotErr(e)) { setSpecial(s); }
      else toast.error(errMsg(e), { duration: 7000, style: { whiteSpace: "pre-line" } });
    } finally { setBusy(false); }
  };
  const remaining = data?.remaining_minutes || detail?.minutes_remaining || 0;
  if (remaining <= 0) return null;
  return (
    <div className="rounded-lg border border-secondary/40 bg-accent/30 p-3 space-y-2"
      data-testid="adjacent-suggestions-panel">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
        <RefreshCcw className="h-3 w-3" />
        Adjacent replacement / double lesson · {remaining} min owed
      </p>
      {loading ? <Spinner /> : !data?.suggestions?.length ? (
        <p className="text-xs text-muted-foreground">No adjacent windows found in the next 60 days (2-month frame).</p>
      ) : (
        <div className="space-y-1.5" data-testid="adjacent-suggestions-list">
          {data.suggestions.slice(0, 8).map((s, i) => (
            <div key={i} className={`rounded-md border p-2 text-xs flex items-center gap-2 ${
              s.clean ? "bg-card" : s.conflict_with_student ? "bg-amber-50 border-amber-300" : "bg-rose-50 border-rose-200"
            }`} data-testid={`adjacent-suggestion-${i}`}>
              <span className="font-mono text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary/20">
                {s.kind}
              </span>
              <span className="font-mono">{s.date} {s.start_time}–{s.end_time}</span>
              <span className="text-muted-foreground text-[10px]">
                → combined {s.combined_span.start_time}–{s.combined_span.end_time}
              </span>
              {s.outside_normal_availability && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold"
                  data-testid={`adjacent-outside-${i}`}>
                  outside normal hours
                </span>
              )}
              <span className="flex-1" />
              {s.clean && (
                <Button size="sm" variant="outline" className="h-7" disabled={busy}
                  onClick={() => bookClean(s)} data-testid={`adjacent-book-${i}`}>
                  Propose
                </Button>
              )}
              {s.conflict_with_student && (
                <>
                  <span className="text-[10px] text-amber-800">
                    Clashes with {s.conflict_with_student.student_name}
                  </span>
                  <Button size="sm" variant="secondary" className="h-7"
                    onClick={() => { setSwapCandidate(s); setSwapOpen(true); }}
                    data-testid={`adjacent-swap-${i}`}>
                    Propose swap
                  </Button>
                </>
              )}
              {!s.clean && !s.conflict_with_student && (
                <span className="text-[10px] text-rose-700">Conflict — try another</span>
              )}
            </div>
          ))}
        </div>
      )}
      <SwapShiftDialog open={swapOpen} onOpenChange={setSwapOpen}
        candidate={swapCandidate} requestId={requestId} onDone={load} />
      <SpecialSlotDialog open={!!special} busy={busy}
        onConfirm={() => special && bookClean(special, true)}
        onCancel={() => setSpecial(null)} />
    </div>
  );
}


export function SourceBadge({ source }) {
  const label = SOURCE_LABELS[source];
  if (!label) return null;
  return (
    <span className="text-xs px-2 py-0.5 rounded-full bg-[#FFF3E0] text-[#B45309] font-semibold"
      data-testid={`source-badge-${source}`}>
      {label}
    </span>
  );
}

export function LeaveNoticesPanel({ role }) {
  const { data: notices, loading, reload } = useList("/leave-notices");
  if (loading || !notices) return null;
  const pending = notices.filter((n) => !n.acknowledged);
  if (pending.length === 0) return null;

  const ack = async (id) => {
    try {
      await api.post(`/leave-notices/${id}/acknowledge`);
      toast.success("Notice acknowledged — thank you. Replacement (if applicable) is arranged separately.");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const catLabel = { standard: "Leave", mc: "MC", emergency: "Emergency Leave" };
  return (
    <div className="rounded-lg border border-[#B45309]/40 bg-[#FFF8EF] p-4 mb-6" data-testid="leave-notices-panel">
      <p className="text-sm font-semibold flex items-center gap-1.5 text-[#B45309] mb-2">
        <AlertTriangle className="h-4 w-4" />Teacher leave notices — please acknowledge
      </p>
      <div className="space-y-2">
        {pending.map((n) => (
          <div key={n.id} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`notice-${n.id}`}>
            <div className="flex-1 min-w-[240px]">
              <p>
                <span className="font-medium">{n.student_name}</span>'s {n.is_group ? "group class" : "lesson"} on{" "}
                <span className="font-mono">{n.class_date} {n.start_time}–{n.end_time}</span> is affected —
                Teacher {n.teacher_name} is on {catLabel[n.leave_category] || "leave"}.
              </p>
              <p className="text-xs text-muted-foreground">
                {n.is_group
                  ? "The school will update the class-level arrangement."
                  : "Replacement will be proposed by the teacher if applicable."}
              </p>
            </div>
            {role === "parent" ? (
              <Button size="sm" onClick={() => ack(n.id)} data-testid={`acknowledge-notice-${n.id}`}>
                <Check className="h-3.5 w-3.5 mr-1" />Acknowledge notice
              </Button>
            ) : (
              <span className="text-xs text-destructive font-semibold">Not acknowledged yet</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ list page
export function RescheduleListPage({ role }) {
  const base = role === "teacher" ? "/teacher/reschedule" : "/parent/reschedule";
  const { data: requests, loading, reload } = useList("/reschedule-requests");
  const { data: schedules } = useList("/schedules");
  const { data: quota, reload: reloadQuota } = useList("/reschedule/quota");
  const [open, setOpen] = useState(false);
  const [classId, setClassId] = useState("");
  const [originalDate, setOriginalDate] = useState("");
  const [reason, setReason] = useState("");
  const [slot, setSlot] = useState(null);
  const [askTeacher, setAskTeacher] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async () => {
    if (!classId || !originalDate || !reason || (!askTeacher && !slot)) {
      toast.error(askTeacher ? "Please complete all fields" : "Please complete all fields and pick an available slot");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/reschedule-requests", {
        class_schedule_id: classId, original_date: originalDate, reason,
        proposed_date: askTeacher ? null : slot.date,
        proposed_start_time: askTeacher ? null : slot.start_time,
        proposed_end_time: askTeacher ? null : slot.end_time,
        proposed_studio_id: askTeacher ? null : slot.studio_id,
      });
      setOpen(false);
      setClassId(""); setOriginalDate(""); setReason(""); setSlot(null); setAskTeacher(false);
      reload(); reloadQuota();
      if (data.status === "draft") {
        toast.warning("Quota exceeded — request saved and flagged for admin override.");
      } else if (data.status === "awaiting_teacher_proposal") {
        toast.success("Request sent — the teacher will propose available days and times.");
        navigate(`${base}/${data.id}`);
      } else {
        toast.success("Request submitted — the other party will respond in the discussion.");
        navigate(`${base}/${data.id}`);
      }
    } catch (e) {
      toast.error(errMsg(e), { duration: 7000, style: { whiteSpace: "pre-line" } });
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="reschedule-list-page">
      <PageHeader title="Reschedule requests"
        description="Discuss and agree a new slot with the other party — mutually agreed times auto-confirm once the system validates availability."
        actions={<Button onClick={() => setOpen(true)} data-testid="new-reschedule-button"><RefreshCcw className="h-4 w-4 mr-1" />New request</Button>} />
      <LeaveNoticesPanel role={role} />
      {role === "teacher" && (requests || []).filter((r) =>
        ["awaiting_teacher_proposal", "submitted", "pending_teacher_response", "under_discussion"].includes(r.status)
        && (r.minutes_remaining ?? 0) > 0).length > 0 && (
        <Link to="/teacher/replacement-assistant"
          className="block rounded-lg border border-secondary/60 bg-accent/60 p-4 mb-4 lift"
          data-testid="replacement-assistant-banner">
          <p className="text-sm font-semibold flex items-center gap-1.5">
            <CalendarClock className="h-4 w-4 text-secondary" />
            Replacement Assistant —{" "}
            {(requests || []).filter((r) =>
              ["awaiting_teacher_proposal", "submitted", "pending_teacher_response", "under_discussion"].includes(r.status)
              && (r.minutes_remaining ?? 0) > 0).length}{" "}
            student(s) waiting for a replacement time
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Smart suggestions are prepared for every affected student — review, adjust and send them all in one go.
          </p>
        </Link>
      )}
      <SwapShiftInbox role={role} />
      <QuotaBanner quota={quota} />
      {loading ? <Spinner full /> : !requests || requests.length === 0 ? <EmptyState text="No reschedule requests yet." /> : (
        <div className="space-y-3 stagger-in">
          {requests.map((r) => (
            <Link key={r.id} to={`${base}/${r.id}`} className="block rounded-lg border bg-card p-5 lift" data-testid={`reschedule-${r.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[220px]">
                  <p className="text-sm font-medium flex items-center gap-2 flex-wrap">
                    {r.student_name} · {r.course_name} with {r.teacher_name}
                    <SourceBadge source={r.source_type} />
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5">
                    <span className="font-mono">{r.original_date} {r.original_start_time}</span>
                    <ArrowRight className="h-3 w-3" />
                    <span className="font-mono">
                      {r.final_agreed_date ? `${r.final_agreed_date} ${r.final_agreed_start_time}` : "under discussion"}
                    </span>
                  </p>
                  <p className="text-xs mt-1">{r.reason}</p>
                </div>
                {r.unread_messages > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-secondary"
                    data-testid={`unread-messages-${r.id}`}>
                    <MessageCircle className="h-3.5 w-3.5" />{r.unread_messages} new
                  </span>
                )}
                {r.quota_exceeded && !r.quota_override_used && <StatusBadge status="overdue" testId={`quota-flag-${r.id}`} />}
                <StatusBadge status={r.status} testId={`reschedule-status-${r.id}`} />
              </div>
            </Link>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="new-reschedule-dialog">
          <DialogHeader><DialogTitle className="font-display">Request a reschedule</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Class to move</Label>
              <Select value={classId} onValueChange={(v) => { setClassId(v); setSlot(null); setOriginalDate(""); }}>
                <SelectTrigger data-testid="reschedule-class-select"><SelectValue placeholder="Select class..." /></SelectTrigger>
                <SelectContent>
                  {(schedules || []).filter((s) => s.status === "active").map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.student_name} · {s.course_name} ({dayName(s.day_of_week)} {s.start_time})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <OccurrencePicker scheduleId={classId} value={originalDate} onChange={setOriginalDate} />
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Reason</Label>
              <Textarea data-testid="reschedule-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
            {classId && role !== "teacher" && (
              <label className="flex items-start gap-2.5 rounded-md border p-3 cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={askTeacher}
                  onChange={(e) => { setAskTeacher(e.target.checked); setSlot(null); }}
                  data-testid="ask-teacher-checkbox" />
                <span className="text-sm">
                  <span className="font-semibold">Can't find a suitable time?</span>{" "}
                  <span className="text-muted-foreground">Skip proposing — the teacher will propose available days and times for you to accept or counter-propose.</span>
                </span>
              </label>
            )}
            {classId && !askTeacher && (
              <div className="border-t pt-4">
                <p className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                  <CalendarClock className="h-4 w-4 text-secondary" />Propose an available slot
                </p>
                <SlotPicker scheduleId={classId} value={slot} onChange={setSlot} showStudio={role === "teacher"}
                  holdOnSelect={role !== "teacher"} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="submit-reschedule-button">
              {busy ? "Submitting..." : "Submit request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------------------------------------------------------------ teacher accept: assign studio
function AcceptStudioDialog({ requestId, proposal, onOpenChange, onDone }) {
  const [studios, setStudios] = useState(null);
  const [studioId, setStudioId] = useState("");
  const [busy, setBusy] = useState(false);
  const [special, setSpecial] = useState(false);

  useEffect(() => {
    if (!proposal) return;
    setStudios(null); setStudioId("");
    api.get(`/reschedule-requests/${requestId}/free-studios`, {
      params: { date: proposal.proposed_date, start_time: proposal.proposed_start_time, end_time: proposal.proposed_end_time },
    }).then((r) => setStudios(r.data.studios)).catch((e) => { toast.error(errMsg(e)); setStudios([]); });
  }, [proposal, requestId]);

  const confirm = async (openSpecial = false) => {
    if (!studioId) { toast.error("Select a studio for this replacement"); return; }
    setBusy(true);
    try {
      await api.post(`/reschedule-proposals/${proposal.id}/accept`, { studio_id: studioId, open_special_slot: openSpecial });
      toast.success("Accepted! Studio assigned — replacement confirmed and timetables updated.");
      setSpecial(false);
      onOpenChange(false); onDone?.();
    } catch (e) {
      if (isSpecialSlotErr(e)) { setSpecial(true); }
      else toast.error(errMsg(e), { duration: 7000, style: { whiteSpace: "pre-line" } });
    }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!proposal} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="accept-studio-dialog">
        <DialogHeader><DialogTitle className="font-display">Accept & assign studio</DialogTitle></DialogHeader>
        {proposal && (
          <>
            <p className="text-sm text-muted-foreground">
              Accepting{" "}
              <span className="font-mono font-semibold text-foreground">
                {proposal.proposed_date} · {proposal.proposed_start_time}–{proposal.proposed_end_time}
              </span>. The family picked the time — you choose the room. Only unoccupied studios are listed.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Studio</Label>
              {!studios ? <Spinner /> : studios.length === 0 ? (
                <p className="text-sm text-destructive border border-dashed rounded-md p-3" data-testid="no-free-studios">
                  No studio is free at this time — decline the proposal and suggest another time.
                </p>
              ) : (
                <Select value={studioId} onValueChange={setStudioId}>
                  <SelectTrigger data-testid="accept-studio-select"><SelectValue placeholder="Select a free studio..." /></SelectTrigger>
                  <SelectContent>
                    {studios.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => confirm(false)} disabled={busy || !studioId} data-testid="confirm-accept-studio-button">
            {busy ? "Confirming..." : "Accept & confirm"}
          </Button>
        </DialogFooter>
        <SpecialSlotDialog open={special} busy={busy}
          onConfirm={() => confirm(true)} onCancel={() => setSpecial(false)} />
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------ detail / discussion page
export function RescheduleDetailPage({ role }) {
  const { id } = useParams();
  const { user } = useAuth();
  const base = role === "teacher" ? "/teacher/reschedule" : "/parent/reschedule";
  const { data: detail, loading, reload } = useList(`/reschedule-requests/${id}`);
  const [message, setMessage] = useState("");
  const [counterOpen, setCounterOpen] = useState(false);
  const [counterSlot, setCounterSlot] = useState(null);
  const [counterMsg, setCounterMsg] = useState("");
  const [finalOpen, setFinalOpen] = useState(false);
  const [finalSlot, setFinalSlot] = useState(null);
  const [finalNote, setFinalNote] = useState("");
  const [extOpen, setExtOpen] = useState(false);
  const [extDate, setExtDate] = useState("");
  const [extMinutes, setExtMinutes] = useState("15");
  const [slotsOpen, setSlotsOpen] = useState(false);
  const [acceptTarget, setAcceptTarget] = useState(null);
  const [specialCounter, setSpecialCounter] = useState(false);
  const [busy, setBusy] = useState(false);

  if (loading || !detail) return <Spinner full />;
  const myGroup = detail.viewer_side; // parent | teacher | staff
  const active = ["submitted", "pending_teacher_response", "pending_parent_response", "under_discussion",
    "awaiting_teacher_proposal"].includes(detail.status);
  const directDiscussion = detail.status === "pending_direct_discussion";

  const act = async (fn, success) => {
    setBusy(true);
    try {
      await fn();
      toast.success(success);
      reload();
    } catch (e) {
      toast.error(errMsg(e), { duration: 7000, style: { whiteSpace: "pre-line" } });
    } finally { setBusy(false); }
  };

  const accept = (p) => {
    if (myGroup === "teacher" && !p.proposed_studio_id) { setAcceptTarget(p); return; }
    act(() => api.post(`/reschedule-proposals/${p.id}/accept`),
      "Agreed! Replacement confirmed — timetables updated.");
  };
  const decline = (p) => {
    const reason = window.prompt("Why doesn't this time work? (optional)") ?? "";
    act(() => api.post(`/reschedule-proposals/${p.id}/reject`, { reason }), "Proposal declined — propose another time.");
  };
  const counter = async (openSpecial = false) => {
    if (!counterSlot) { toast.error("Pick an available slot first"); return; }
    setBusy(true);
    try {
      await api.post(`/reschedule-requests/${id}/proposals`, {
        proposed_date: counterSlot.date, proposed_start_time: counterSlot.start_time,
        proposed_end_time: counterSlot.end_time, proposed_studio_id: counterSlot.studio_id || null,
        message: counterMsg || null, open_special_slot: openSpecial,
      });
      toast.success("Counter-proposal sent.");
      setCounterOpen(false); setCounterSlot(null); setCounterMsg(""); setSpecialCounter(false);
      reload();
    } catch (e) {
      if (isSpecialSlotErr(e)) { setSpecialCounter(true); }
      else toast.error(errMsg(e), { duration: 7000, style: { whiteSpace: "pre-line" } });
    } finally { setBusy(false); }
  };
  const confirmFinal = () => {
    if (!finalSlot) { toast.error("Pick an available slot first"); return; }
    act(async () => {
      await api.post(`/reschedule-requests/${id}/confirm-final`, {
        date: finalSlot.date, start_time: finalSlot.start_time,
        end_time: finalSlot.end_time, studio_id: finalSlot.studio_id,
        note: finalNote || null,
      });
      setFinalOpen(false); setFinalSlot(null); setFinalNote("");
    }, "Final arrangement confirmed — timetables updated.");
  };
  const addExtension = () => {
    if (!extDate) { toast.error("Pick the lesson date the extension attaches to"); return; }
    act(async () => {
      await api.post(`/reschedule-requests/${id}/extensions`, {
        attach_class_schedule_id: detail.original_class_schedule_id,
        date: extDate, minutes: parseInt(extMinutes, 10),
      });
      setExtOpen(false); setExtDate("");
    }, "Extension scheduled — replacement minutes updated.");
  };
  const sendMessage = () => {
    if (!message.trim()) return;
    act(async () => {
      await api.post(`/reschedule-requests/${id}/messages`, { message: message.trim() });
      setMessage("");
    }, "Message sent");
  };
  const cancel = () => {
    if (!window.confirm("Cancel this reschedule request?")) return;
    act(() => api.post(`/reschedule-requests/${id}/cancel`), "Request cancelled");
  };

  const timeline = [
    ...detail.proposals.map((p) => ({ kind: "proposal", at: p.created_at, item: p })),
    ...detail.messages.map((m) => ({ kind: "message", at: m.created_at, item: m })),
  ].sort((a, b) => (a.at < b.at ? -1 : 1));

  return (
    <div data-testid="reschedule-detail-page">
      <Link to={base} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> All requests
      </Link>
      <PageHeader
        title={`${detail.student_name} · ${detail.course_name}`}
        description={`With ${detail.teacher_name} · requested by ${detail.requested_by_name} · ${detail.reason}`}
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge status={detail.status} testId="detail-status-badge" />
            {(detail.requested_by_user_id === user.id) && active && (
              <Button variant="outline" size="sm" className="text-destructive border-destructive/30" onClick={cancel} data-testid="cancel-request-button">
                Cancel request
              </Button>
            )}
          </div>
        } />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="rounded-lg border bg-card p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Original lesson</p>
          <p className="font-mono text-sm">{detail.original_date} · {detail.original_start_time}–{detail.original_end_time}</p>
          <p className="text-xs text-muted-foreground">{detail.original_studio_name}</p>
        </div>
        <div className={`rounded-lg border p-4 ${detail.final_agreed_date ? "bg-accent border-primary/30" : "bg-card"}`}>
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">
            {detail.final_agreed_date ? "Agreed new slot" : "New slot — under discussion"}
          </p>
          {detail.final_agreed_date ? (
            <>
              <p className="font-mono text-sm" data-testid="final-agreed-slot">
                {detail.final_agreed_date} · {detail.final_agreed_start_time}–{detail.final_agreed_end_time}
              </p>
              <p className="text-xs text-muted-foreground">{detail.final_agreed_studio_name}</p>
            </>
          ) : <p className="text-sm text-muted-foreground">Waiting for both parties to agree</p>}
          <div className="flex gap-3 mt-2 text-xs">
            <span className={detail.agreed_by_parent ? "text-[#2D6A4F] font-semibold" : "text-muted-foreground"}>
              {detail.agreed_by_parent ? "✓" : "○"} Parent
            </span>
            <span className={detail.agreed_by_teacher ? "text-[#2D6A4F] font-semibold" : "text-muted-foreground"}>
              {detail.agreed_by_teacher ? "✓" : "○"} Teacher
            </span>
          </div>
        </div>
        <MinuteLedgerCard detail={detail} viewerSide={myGroup} onAddExtension={() => setExtOpen(true)} />
      </div>

      {detail.conflicts?.length > 0 && (
        <div className="rounded-lg border border-destructive/40 bg-[#FFEDEC] p-4 mb-6 text-sm" data-testid="conflict-warning">
          <p className="font-semibold flex items-center gap-1.5 text-destructive mb-1">
            <AlertTriangle className="h-4 w-4" />Current slot has conflicts
          </p>
          {detail.conflicts.map((c, i) => <p key={i} className="text-xs">{c}</p>)}
        </div>
      )}

      {detail.status === "draft" && detail.quota_exceeded && (
        <div className="rounded-lg border border-destructive/40 bg-[#FFEDEC] p-4 mb-6 text-sm" data-testid="quota-blocked-banner">
          This request exceeded the reschedule quota and is waiting for an admin override before the discussion can begin.
        </div>
      )}

      {detail.status === "awaiting_teacher_proposal" && (
        <div className="rounded-lg border border-[#B45309]/40 bg-[#FFF8EF] p-4 mb-6 text-sm space-y-2" data-testid="awaiting-proposal-banner">
          <p>{myGroup === "teacher"
            ? "This replacement request needs your proposal. Propose a time to this family directly, or open replacement slots for your affected students to claim (first come, first served)."
            : "The teacher will propose a replacement time, or open slots you can claim below."}</p>
          {myGroup === "teacher" && (
            <Button size="sm" variant="outline" onClick={() => setSlotsOpen(true)} data-testid="open-slots-button">
              <CalendarClock className="h-4 w-4 mr-1" />Open replacement slots
            </Button>
          )}
        </div>
      )}

      {["awaiting_teacher_proposal", "pending_parent_response", "pending_teacher_response",
        "under_discussion", "pending_direct_discussion", "submitted"].includes(detail.status) && (
        <OpenSlotsPanel detail={detail} role={role} onClaimed={reload} />
      )}

      {directDiscussion && (
        <div className="rounded-lg border border-[#B45309]/40 bg-[#FFF8EF] p-4 mb-6 text-sm space-y-2" data-testid="direct-discussion-banner">
          <p className="font-semibold">Pending direct discussion</p>
          <p>The proposal round limit was reached without agreement. Please agree a time directly in the
            message thread below (or face-to-face during the lesson). {myGroup === "teacher"
              ? "Once agreed, enter the final arrangement here — the system will validate and confirm it."
              : "Once agreed, your teacher (or the school) will enter the final arrangement."}</p>
          {myGroup === "teacher" && (
            <Button size="sm" onClick={() => setFinalOpen(true)} data-testid="confirm-final-button">
              <Check className="h-4 w-4 mr-1" />Enter final agreed arrangement
            </Button>
          )}
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold">Discussion</h3>
        {active && myGroup !== "staff" && (
          <Button size="sm" variant="outline" onClick={() => setCounterOpen(true)} data-testid="counter-propose-button">
            <CalendarClock className="h-4 w-4 mr-1" />Propose another time
          </Button>
        )}
      </div>
      <div className="space-y-3 mb-6">
        {timeline.length === 0 && <EmptyState text="No discussion yet." />}
        {timeline.map(({ kind, item }) => kind === "proposal" ? (
          <div key={item.id} className="rounded-lg border bg-card p-4" data-testid={`proposal-${item.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px]">
                <p className="text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">{item.proposed_by_name}</span> ({item.proposed_by_role}) proposed
                </p>
                <p className="font-mono text-sm mt-0.5">
                  {item.proposed_date} · {item.proposed_start_time}–{item.proposed_end_time}
                  {item.proposed_studio_name
                    ? ` · ${item.proposed_studio_name}`
                    : " · studio assigned on teacher acceptance"}
                </p>
                {item.is_special_slot && (
                  <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold"
                    data-testid={`special-slot-badge-${item.id}`}>
                    ★ Teacher special one-time slot
                  </span>
                )}
                {item.rejection_reason && <p className="text-xs text-destructive mt-1">Declined: {item.rejection_reason}</p>}
              </div>
              <StatusBadge status={item.proposal_status === "open" ? "pending" : item.proposal_status === "accepted" ? "approved" : item.proposal_status} />
              {item.proposal_status === "open" && active === false && null}
              {item.proposal_status === "open" && myGroup !== "staff" && item.proposed_by_role !== myGroup && (
                <div className="flex gap-2">
                  <Button size="sm" disabled={busy} onClick={() => accept(item)} data-testid={`accept-proposal-${item.id}`}>
                    <Check className="h-3.5 w-3.5 mr-1" />Accept
                  </Button>
                  {/* Teachers cannot outright reject a parent proposal — they must counter-propose instead */}
                  {!(myGroup === "teacher" && item.proposed_by_role === "parent") && (
                    <Button size="sm" variant="outline" className="text-destructive border-destructive/30" disabled={busy}
                      onClick={() => decline(item)} data-testid={`decline-proposal-${item.id}`}>
                      <X className="h-3.5 w-3.5 mr-1" />Decline
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div key={item.id} className={`rounded-lg border p-3 text-sm max-w-xl ${item.sender_user_id === user.id ? "ml-auto bg-accent" : "bg-card"}`}
            data-testid={`message-${item.id}`}>
            <p className="text-xs text-muted-foreground mb-0.5">{item.sender_name} · {item.created_at?.slice(0, 16).replace("T", " ")}</p>
            <p>{item.message}</p>
          </div>
        ))}
      </div>

      {(active || directDiscussion || detail.status === "pending_admin_approval") && (
        <div className="flex gap-2" data-testid="message-composer">
          <Input data-testid="message-input" placeholder="Write a message to the other party..." value={message}
            onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMessage()} />
          <Button onClick={sendMessage} disabled={!message.trim() || busy} data-testid="send-message-button">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      )}

      <Dialog open={counterOpen} onOpenChange={setCounterOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="counter-proposal-dialog">
          <DialogHeader><DialogTitle className="font-display">Propose another time</DialogTitle></DialogHeader>
          <SlotPicker scheduleId={detail.original_class_schedule_id} requestId={id} value={counterSlot}
            onChange={setCounterSlot} showStudio={myGroup === "teacher"} holdOnSelect={myGroup === "parent"} />
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Message (optional)</Label>
            <Textarea data-testid="counter-message-input" rows={2} value={counterMsg} onChange={(e) => setCounterMsg(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCounterOpen(false)}>Cancel</Button>
            <Button onClick={counter} disabled={busy} data-testid="submit-counter-button">Send proposal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {myGroup === "teacher" && (
        <OpenSlotsDialog detail={detail} open={slotsOpen} onOpenChange={setSlotsOpen} onDone={reload} />
      )}
      {myGroup === "teacher" && (detail.minutes_remaining ?? 0) > 0 && (
        <AdjacentSuggestionsPanel requestId={id} detail={detail} onDone={reload} />
      )}

      <AcceptStudioDialog requestId={id} proposal={acceptTarget}
        onOpenChange={(v) => !v && setAcceptTarget(null)}
        onDone={() => { setAcceptTarget(null); reload(); }} />

      <Dialog open={extOpen} onOpenChange={setExtOpen}>
        <DialogContent className="max-w-md" data-testid="extension-dialog">
          <DialogHeader><DialogTitle className="font-display">Add replacement extension</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Extends the student's regular {detail.original_start_time}–{detail.original_end_time} lesson on the
            selected date. Example: 30-min lesson + 15-min extension = 45-min total session.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Lesson date</Label>
              <Input type="date" value={extDate} onChange={(e) => setExtDate(e.target.value)} data-testid="extension-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Extension minutes</Label>
              <Select value={extMinutes} onValueChange={setExtMinutes}>
                <SelectTrigger data-testid="extension-minutes-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[15, 30, 45].filter((m) => m <= (detail.minutes_remaining ?? 45)).map((m) => (
                    <SelectItem key={m} value={String(m)}>{m} minutes</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtOpen(false)}>Cancel</Button>
            <Button onClick={addExtension} disabled={busy} data-testid="submit-extension-button">Schedule extension</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={finalOpen} onOpenChange={setFinalOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="confirm-final-dialog">
          <DialogHeader><DialogTitle className="font-display">Enter final agreed arrangement</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Use this after you and the family have mutually agreed a time. The system validates teacher,
            student and studio availability, then confirms without admin approval.
          </p>
          <SlotPicker scheduleId={detail.original_class_schedule_id} requestId={id} value={finalSlot} onChange={setFinalSlot} />
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Agreement note (optional)</Label>
            <Textarea data-testid="final-note-input" rows={2} value={finalNote} onChange={(e) => setFinalNote(e.target.value)}
              placeholder="e.g. Agreed with parent during today's lesson" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFinalOpen(false)}>Cancel</Button>
            <Button onClick={confirmFinal} disabled={busy} data-testid="submit-final-button">Confirm arrangement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
